import { hasText } from '~/utils';
import type { SelectOption } from '~/components/ui/inputs/selectOptions';

export type BranchCoded = { branchCode?: string | null };

export function branchCodeOf(location: BranchCoded): string | null {
  return hasText(location.branchCode) ? location.branchCode.trim() : null;
}

function distinctBranchCodes(locations: BranchCoded[]): Set<string> {
  return new Set(
    locations
      .map(branchCodeOf)
      .filter((branchCode): branchCode is string => branchCode !== null)
  );
}

// Single-branch organizations (most small sellers, whose branch codes were backfilled from
// their location codes) get a flat list rather than one lone group header.
export function withBranchGroups<T extends BranchCoded>(
  locations: T[],
  toOption: (location: T) => { value: string; label: string }
): SelectOption[] {
  const grouped = distinctBranchCodes(locations).size > 1;
  return locations.map((location) => {
    const option = toOption(location);
    const branchCode = grouped ? branchCodeOf(location) : null;
    return branchCode === null ? option : { ...option, group: branchCode };
  });
}

export type BranchGroup<T> = { branchCode: string | null; locations: T[] };

export function groupLocationsByBranch<T extends BranchCoded>(
  locations: T[]
): BranchGroup<T>[] {
  const groups: BranchGroup<T>[] = [];
  const byBranch = new Map<string, BranchGroup<T>>();
  for (const location of locations) {
    const branchCode = branchCodeOf(location);
    const key = branchCode ?? '__no_branch__';
    const existing = byBranch.get(key);
    if (existing) {
      existing.locations.push(location);
    } else {
      const group: BranchGroup<T> = { branchCode, locations: [location] };
      byBranch.set(key, group);
      groups.push(group);
    }
  }
  return groups;
}
